import {
  BarChart3,
  Check,
  ChevronsUpDown,
  Code2,
  CreditCard,
  FlaskConical,
  ImageIcon,
  LayoutTemplate,
  LogOut,
  Palette,
  Plug,
  Plus,
  Settings,
  ShieldCheck,
  Type,
  User,
  Workflow,
  Menu as MenuIcon,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toastError } from '../lib/errors';
import { cn, formatDate, formatNumber } from '../lib/format';
import { useT } from '../lib/i18n';
import { LanguageSwitcher } from './LanguageSwitcher';
import { queryClient, refreshSession, useWorkspaceInfo } from '../lib/queries';
import { useCurrentWorkspace, useSession } from '../lib/session';
import { Logo } from './Logo';
import {
  Button,
  Dialog,
  Field,
  Input,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  Progress,
} from './ui';

function NavItem({
  to,
  icon: Icon,
  children,
  end,
}: {
  to: string;
  icon: typeof LayoutTemplate;
  children: React.ReactNode;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition',
          isActive
            ? 'bg-white text-zinc-900 shadow-xs ring-1 ring-zinc-200'
            : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900',
        )
      }
    >
      <Icon className="size-4 shrink-0" />
      {children}
    </NavLink>
  );
}

function WorkspaceSwitcher() {
  const t = useT();
  const navigate = useNavigate();
  const workspaces = useSession((s) => s.workspaces);
  const current = useCurrentWorkspace();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <>
      <Menu>
        <MenuTrigger asChild>
          <button className="flex w-full items-center gap-2.5 rounded-xl border border-zinc-200 bg-white px-2.5 py-2 text-left shadow-xs hover:border-zinc-300">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-500 text-sm font-bold text-white">
              {current?.name.slice(0, 1).toUpperCase() ?? 'W'}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-zinc-900">{current?.name}</div>
              <div className="text-xs text-zinc-500 capitalize">
                {current?.plan} · {current && t(`role.${current.role}`)}
              </div>
            </div>
            <ChevronsUpDown className="size-4 text-zinc-400" />
          </button>
        </MenuTrigger>
        <MenuContent align="start">
          <MenuLabel>{t('nav.workspaces')}</MenuLabel>
          {workspaces.map((w) => (
            <MenuItem
              key={w.id}
              onSelect={() => {
                useSession.getState().setWorkspace(w.id);
                queryClient.clear();
                navigate('/app/templates');
              }}
              icon={w.id === current?.id ? <Check /> : <span className="inline-block size-4" />}
            >
              {w.name}
            </MenuItem>
          ))}
          <MenuSeparator />
          <MenuItem icon={<Plus />} onSelect={() => setCreating(true)}>
            {t('nav.newWorkspace')}
          </MenuItem>
        </MenuContent>
      </Menu>
      <Dialog
        open={creating}
        onOpenChange={setCreating}
        title={t('nav.newWorkspace')}
        size="sm"
        footer={
          <Button
            variant="primary"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const ws = await api<{ id: string }>('/api/workspaces', { body: { name } });
                await refreshSession();
                useSession.getState().setWorkspace(ws.id);
                queryClient.clear();
                setCreating(false);
                setName('');
                navigate('/app/templates');
              } catch (err) {
                toastError(err);
              } finally {
                setBusy(false);
              }
            }}
          >
            {t('common.create')}
          </Button>
        }
      >
        <Field label={t('common.name')}>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Marketing"
          />
        </Field>
      </Dialog>
    </>
  );
}

function UsageCard() {
  const t = useT();
  const { data } = useWorkspaceInfo();
  if (!data) return null;
  const { used, limit } = data.usage.renders;
  const pct = (used / limit) * 100;
  return (
    <Link
      to="/app/settings/billing"
      className="block rounded-xl border border-zinc-200 bg-white p-3 hover:border-zinc-300"
    >
      <div className="mb-2 flex items-center justify-between text-xs">
        <span className="font-medium text-zinc-700">{t('nav.rendersThisMonth')}</span>
        <span className="text-zinc-500 tabular-nums">
          {formatNumber(used)} / {formatNumber(limit)}
        </span>
      </div>
      <Progress value={pct} tone={pct >= 100 ? 'red' : pct > 80 ? 'amber' : 'brand'} />
      {data.plan === 'free' && (
        <div className="mt-2 text-xs font-semibold text-brand-600">{t('nav.upgrade')} →</div>
      )}
    </Link>
  );
}

function UserMenu() {
  const t = useT();
  const user = useSession((s) => s.user);
  const navigate = useNavigate();
  return (
    <Menu>
      <MenuTrigger asChild>
        <button className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left hover:bg-zinc-100">
          <div className="flex size-7 items-center justify-center rounded-full bg-zinc-200 text-xs font-semibold text-zinc-700">
            {user?.name.slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-zinc-800">{user?.name}</div>
            <div className="truncate text-xs text-zinc-500">{user?.email}</div>
          </div>
        </button>
      </MenuTrigger>
      <MenuContent align="start">
        <MenuItem icon={<User />} onSelect={() => navigate('/app/settings/profile')}>
          {t('nav.profile')}
        </MenuItem>
        <MenuSeparator />
        <MenuItem
          icon={<LogOut />}
          onSelect={async () => {
            await api('/api/auth/logout', { method: 'POST', workspace: false });
            useSession.getState().setSession(null, []);
            queryClient.clear();
            navigate('/login');
          }}
        >
          {t('nav.logout')}
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

/** A payment failed: the plan keeps working during the grace period. */
function GraceBanner() {
  const t = useT();
  const { data } = useWorkspaceInfo();
  const until = data?.graceUntil ? new Date(data.graceUntil) : null;
  if (!until || until <= new Date() || !['owner', 'admin'].includes(data!.role)) return null;
  return (
    <div
      className="flex items-center justify-center gap-2 border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-900"
      data-testid="grace-banner"
    >
      <CreditCard className="size-4 shrink-0 text-red-600" />
      {t('billing.graceBanner', { date: formatDate(until, false) })}
      <Link to="/app/settings/billing" className="font-semibold underline hover:no-underline">
        {t('billing.graceBannerLink')}
      </Link>
    </div>
  );
}

/** Reminder that renders are watermarked while the workspace is in preview mode. */
function PreviewModeBanner() {
  const t = useT();
  const { data } = useWorkspaceInfo();
  if (!data?.settings.previewMode) return null;
  return (
    <div
      className="flex items-center justify-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900"
      data-testid="preview-mode-banner"
    >
      <FlaskConical className="size-4 shrink-0 text-amber-600" />
      {t('previewMode.banner')}
      <Link to="/app/settings/general" className="font-semibold underline hover:no-underline">
        {t('previewMode.manage')}
      </Link>
    </div>
  );
}

export function AppLayout() {
  const t = useT();
  const superadmin = useSession((s) => s.user?.superadmin);
  // Phones and narrow windows: the sidebar slides in over the page
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMenuOpen(false), [location.pathname]);
  return (
    <div className="flex h-full">
      {menuOpen && (
        <div
          className="fixed inset-0 z-30 bg-zinc-950/30 lg:hidden"
          onClick={() => setMenuOpen(false)}
          aria-hidden
        />
      )}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 shrink-0 flex-col gap-4 overflow-y-auto border-r border-zinc-200 bg-zinc-50 p-3 transition-transform lg:static lg:translate-x-0',
          menuOpen ? 'translate-x-0 shadow-xl' : '-translate-x-full',
        )}
      >
        <div className="flex items-center">
          <Logo to="/app/templates" className="px-2 pt-1" />
          <button
            className="ml-auto rounded-lg p-1.5 text-zinc-500 hover:bg-zinc-200/60 lg:hidden"
            onClick={() => setMenuOpen(false)}
            aria-label={t('common.close')}
          >
            <X className="size-5" />
          </button>
        </div>
        <WorkspaceSwitcher />
        <nav className="flex flex-col gap-0.5">
          <NavItem to="/app/templates" icon={LayoutTemplate}>
            {t('nav.templates')}
          </NavItem>
          <NavItem to="/app/automations" icon={Workflow}>
            {t('nav.automations')}
          </NavItem>
          <NavItem to="/app/renders" icon={BarChart3}>
            {t('nav.renders')}
          </NavItem>
          <NavItem to="/app/assets" icon={ImageIcon}>
            {t('nav.assets')}
          </NavItem>
          <NavItem to="/app/brands" icon={Palette}>
            {t('brand.title')}
          </NavItem>
          <NavItem to="/app/fonts" icon={Type}>
            {t('nav.fonts')}
          </NavItem>
          <div className="mt-3 mb-1 px-2.5 text-[11px] font-semibold tracking-wider text-zinc-400 uppercase">
            {t('nav.developers')}
          </div>
          <NavItem to="/app/api" icon={Code2}>
            {t('nav.api')}
          </NavItem>
          <NavItem to="/app/integrations" icon={Plug}>
            {t('nav.integrations')}
          </NavItem>
          <div className="mt-3 mb-1 px-2.5 text-[11px] font-semibold tracking-wider text-zinc-400 uppercase">
            {t('nav.workspace')}
          </div>
          <NavItem to="/app/settings/general" icon={Settings}>
            {t('nav.settings')}
          </NavItem>
          <NavItem to="/app/settings/billing" icon={CreditCard}>
            {t('nav.billing')}
          </NavItem>
          {superadmin && (
            <NavItem to="/app/admin" icon={ShieldCheck}>
              {t('nav.admin')}
            </NavItem>
          )}
        </nav>
        <div className="flex-1" />
        <UsageCard />
        <LanguageSwitcher className="mx-1 flex" />
        <UserMenu />
      </aside>
      <main className="scrollbar-thin min-w-0 flex-1 overflow-y-auto">
        <div className="sticky top-0 z-20 flex items-center gap-2 border-b border-zinc-200 bg-white/90 px-4 py-2.5 backdrop-blur lg:hidden">
          <button
            className="rounded-lg p-1.5 text-zinc-600 hover:bg-zinc-100"
            onClick={() => setMenuOpen(true)}
            aria-label={t('common.menu')}
            data-testid="open-menu"
          >
            <MenuIcon className="size-5" />
          </button>
          <Logo to="/app/templates" />
        </div>
        <GraceBanner />
        <PreviewModeBanner />
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-8 sm:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
